import { parseFileSize, toFileSize } from '../helpers';
import { FileSize } from '../models';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class FormatValidators {
  /**
   * URL validator
   * More flexible format than Angular's default
   */
  static urlValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      try {
        new URL(control.value);
        return null;
      } catch {
        return { url: { value: control.value } };
      }
    };
  }

  /**
   * Validates a US bank routing number (ABA).
   *
   * Validation rules:
   * - Trims leading and trailing whitespace
   * - Must contain only numeric characters
   * - Must be exactly 9 digits long
   * - Must start with a valid ABA prefix
   * - Must pass the official ABA checksum validation
   *
   * Notes:
   * - This validator does NOT verify that the bank account exists.
   * - This validator does NOT verify that the bank is active.
   * - Use backend or payment provider validation for final verification.
   *
   * @returns {ValidatorFn} Angular validator function
   */
  static usRoutingNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = control.value;

      // Allow empty values; use Validators.required if needed
      if (rawValue == null || rawValue === '') {
        return null;
      }

      const value = String(rawValue).trim();

      // Must contain only digits
      if (!/^\d+$/.test(value)) {
        return { routingNumber: true };
      }

      // Must be exactly 9 digits
      if (value.length !== 9) {
        return { routingNumber: true };
      }

      // Validate ABA prefix (first two digits)
      const prefix = Number(value.substring(0, 2));
      const isValidPrefix =
        (prefix >= 1 && prefix <= 12) ||
        (prefix >= 21 && prefix <= 32) ||
        (prefix >= 61 && prefix <= 72) ||
        prefix === 80;

      if (!isValidPrefix) {
        return { routingNumber: true };
      }

      // ABA checksum validation
      const digits = value.split('').map(Number);

      const checksum =
        3 * (digits[0] + digits[3] + digits[6]) +
        7 * (digits[1] + digits[4] + digits[7]) +
        1 * (digits[2] + digits[5] + digits[8]);

      if (checksum % 10 !== 0) {
        return { routingNumber: true };
      }

      return null;
    };
  }

  /**
   * Phone number format validator
   * Validates that the phone number contains exactly 10 digits (US format)
   * Accepts any format as long as it has 10 digits (ignores formatting characters)
   * Examples: 1234567890, (123) 456-7890, 123-456-7890
   */
  static phone(country: 'US' = 'US'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value || '').toString().trim();

      if (!value) return null;

      switch (country) {
        // United States of America
        case 'US': {
          // Extract only digits from the value
          const digitsOnly = value.replace(/\D/g, '');

          // Check if we have exactly 10 digits
          if (digitsOnly.length !== 10) {
            return { phone: { pattern: '10 digits required' } };
          }

          return null;
        }

        default:
          return null;
      }
    };
  }

  /**
   * Validator that ensures a control's value contains only numeric digits (0–9).
   *
   * The validator accepts strings and numbers; non-string values will be converted
   * to string before validation. By default the validator does not allow spaces,
   * but that behaviour can be changed with the `allowSpaces` parameter.
   *
   * Usage:
   * ```ts
   * this.form = this.fb.group({
   *   code: ['', [onlyNumbers()]],         // no spaces allowed
   *   phone: ['', [onlyNumbers(true)]],    // spaces allowed (e.g. "123 456 789")
   * });
   * ```
   *
   * @param allowSpaces When true, one or more space characters are permitted between digits.
   *                    Default is `true`.
   * @returns A ValidatorFn that returns `null` when the value is valid, otherwise an object:
   *          `{ onlyNumbers: { allowedSpaces: boolean, actualValue: string } }`.
   */
  static onlyNumbers(allowSpaces: boolean = true): ValidatorFn {
    // Pattern matches only digits, optionally allowing spaces.
    const pattern = allowSpaces ? /^[0-9\s]+$/ : /^[0-9]+$/;

    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;

      // Treat empty as valid; "required" should be used to enforce presence.
      if (raw === null || raw === undefined || raw === '') {
        return null;
      }

      // Convert non-string values (e.g., numbers) safely to string for testing.
      const value = typeof raw === 'string' ? raw : String(raw);

      // Test against the pattern.
      if (pattern.test(value)) {
        return null;
      }

      return {
        onlyNumbers: {
          allowedSpaces: allowSpaces,
          actualValue: value
        }
      };
    };
  }

  /**
   * Validates that a File does not exceed the specified maximum size.
   *
   * The validation is performed using the File.size property (in bytes),
   * without reading the file content into memory.
   *
   * @param maxBytes - Maximum allowed file size in bytes.
   * @returns A validator function that returns a `maxFileSize` error
   *          when the file exceeds the allowed size, or `null` if valid.
   */
  static maxFileSize(maxBytes: FileSize) {
    return (control: AbstractControl): ValidationErrors | null => {
      const file = control.value as File[] | null;
      const parsed = parseFileSize(maxBytes);

      if (!file) {
        return null;
      }

      return file.some((f) => f.size > parsed.bytes)
        ? { maxFileSize: { actual: toFileSize(file[0].size), max: maxBytes } }
        : null;
    };
  }

  /**
   * SSN (Social Security Number) validator
   * Validates that the SSN contains exactly 9 digits
   */
  static ssn(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = control.value;
      if (!val) return null;

      const digitsOnly = val.toString().replace(/\D/g, '');
      if (digitsOnly.length !== 9) {
        return { ssn: true };
      }

      return null;
    };
  }

  /**
   * Validates that the number has at most a specified number of decimal places.
   *
   * @param max - Maximum number of decimal places allowed.
   */
  /**
   * Validates that the trimmed value has at least `min` non-whitespace characters.
   * Use alongside Validators.required for note/text fields that must not be blank.
   */
  static minNonWhitespace(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value ?? '';
      return value.trim().length >= min
        ? null
        : { minNonWhitespace: { required: min, actual: value.trim().length } };
    };
  }

  static maxDecimals(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;

      if (raw === null || raw === undefined || raw === '') {
        return null;
      }

      const value = String(raw);

      if (!value.includes('.')) {
        return null;
      }

      const decimals = value.split('.')[1].length;
      return decimals <= max
        ? null
        : { maxDecimals: { actual: decimals, expected: max } };
    };
  }
}
